import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X, Clock, MapPin } from "lucide-react";
import { BUSINESS, NAV, IMG } from "@/data/site";
import SocialLinks from "./Socials";

export default function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header className="sticky top-0 z-50">
      <div className="hidden md:block bg-ink border-b border-line">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between text-xs text-slate-400">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2 font-mono tracking-wide">
              <Clock size={12} className="text-gold" aria-hidden="true" />
              Mon–Sat 5:30 AM–5:00 PM · Sun Closed
            </span>
            <span className="hidden lg:inline">{BUSINESS.languages}</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden lg:flex items-center gap-2 font-mono tracking-wide">
              <MapPin size={12} className="text-gold" aria-hidden="true" />
              Hardin, MT · Free estimates within 100 miles
            </span>
            <a
              href={BUSINESS.emailHref}
              data-testid="header-email-link"
              className="hover:text-gold transition-colors"
            >
              {BUSINESS.email}
            </a>
            <span className="flex items-center gap-1.5 pl-3 border-l border-line">
              <SocialLinks testIdPrefix="header-social" boxClass="w-7 h-7" iconSize={14} />
            </span>
          </div>
        </div>
      </div>

      <div className="bg-ink/95 backdrop-blur-md border-b border-line">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-24 md:h-32 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-button"
              aria-label={open ? "Close menu" : "Open menu"}
              className="xl:hidden inline-flex items-center justify-center w-10 h-10 border border-line text-slate-200 hover:border-gold hover:text-gold transition-colors"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
            <nav className="hidden xl:flex items-center gap-4" aria-label="Main">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `text-[13px] font-medium uppercase tracking-wider whitespace-nowrap transition-colors ${
                      isActive ? "text-gold" : "text-slate-300 hover:text-white"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <a
              href={BUSINESS.phoneHref}
              data-testid="header-call-button"
              className="hidden md:inline-flex items-center gap-2 px-2 py-2 text-sm font-semibold whitespace-nowrap text-slate-100 hover:text-gold transition-colors"
            >
              <Phone size={15} className="text-gold" aria-hidden="true" />
              {BUSINESS.phone}
            </a>
            <Link
              to="/contact"
              data-testid="header-cta-button"
              className="hidden sm:inline-flex items-center bg-gold px-3 py-2.5 text-sm font-bold uppercase tracking-wider whitespace-nowrap text-ink hover:bg-gold-light transition-colors"
            >
              Free Estimate
            </Link>
          </div>

          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 shrink-0">
            <img src={IMG("logo.png")} alt="Park & Spark logo" className="h-20 w-20 md:h-28 md:w-28 object-contain" />
            <span className="leading-none">
              <span className="block font-display text-2xl md:text-3xl font-bold tracking-wide text-slate-100">
                PARK &amp; SPARK
              </span>
              <span className="block text-[10px] font-mono font-bold uppercase tracking-[0.25em] text-gold mt-1.5">
                Mobile Welding, LLC
              </span>
            </span>
          </Link>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[70] xl:hidden" data-testid="mobile-drawer">
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
            className="absolute inset-0 w-full bg-ink/80 backdrop-blur-sm"
          />
          <div className="absolute right-0 top-0 h-full w-80 max-w-[85%] bg-coal border-l border-line p-6 flex flex-col overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <img src={IMG("logo.png")} alt="Park & Spark logo" className="h-10 w-10 object-contain" />
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="mobile-drawer-close-button"
                aria-label="Close menu"
                className="inline-flex items-center justify-center w-10 h-10 border border-line text-slate-200 hover:border-gold hover:text-gold transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="flex flex-col gap-1" aria-label="Mobile">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`drawer-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `px-3 py-3 font-display text-lg font-bold uppercase tracking-wide border-l-2 transition-colors ${
                      isActive
                        ? "text-gold border-gold bg-gold/5"
                        : "text-slate-200 border-transparent hover:border-gold/50"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <div className="mt-6 flex items-center justify-center gap-3">
              <SocialLinks testIdPrefix="drawer-social" boxClass="w-10 h-10" iconSize={16} />
            </div>
            <a
              href={BUSINESS.phoneHref}
              data-testid="drawer-call-button"
              className="mt-6 inline-flex items-center justify-center gap-2 bg-gold px-4 py-3.5 font-bold uppercase tracking-wider text-ink hover:bg-gold-light transition-colors"
            >
              <Phone size={16} aria-hidden="true" />
              Call {BUSINESS.phone}
            </a>
            <p className="mt-4 text-center text-xs text-slate-500">{BUSINESS.languages}</p>
          </div>
        </div>
      )}
    </header>
  );
}
